/**
 * <looks-filter> custom element
 *
 * Two modes, picked from the markup:
 *
 * - Index mode (the Looks page, sections/looks-grid.liquid): the server
 *   renders only the first batch of cards plus a JSON index of every look
 *   (<script data-look-index>). Filtering and batching run on the index, and
 *   any card not already on the page is fetched on demand from
 *   `<look_url>?section_id=look-card-item` (Section Rendering API), so the
 *   server never builds more cards than the shopper is shown.
 * - DOM mode (featured lookbook): every card is server-rendered and this
 *   element filters and reveals them client-side, with no network calls.
 *
 * Markup contract:
 *   <looks-filter
 *     data-list-page-url="/pages/looks"
 *     data-per-page="24"
 *     data-pagination-mode="load_more|infinite|both"
 *   >
 *     <a data-look-filter="">All</a>
 *     <a data-look-filter="girl">Girls</a>
 *     ...
 *     <div data-look-grid>
 *       <article data-look-card data-categories="girl,boy">…</article>
 *       …
 *     </div>
 *     <p data-look-empty hidden>…</p>
 *     <button data-load-more>Load more</button>
 *     <div data-load-sentinel></div>
 *   </looks-filter>
 *
 * URL contract:
 *   ?category=<handle>   (omitted on "All")
 */

if (!customElements.get('looks-filter')) {
  class LooksFilter extends HTMLElement {
    constructor() {
      super();
      this._onFilterClick = this._onFilterClick.bind(this);
      this._onLoadMore = this._onLoadMore.bind(this);
      this._onPopState = this._onPopState.bind(this);
      this._onIntersect = this._onIntersect.bind(this);
    }

    connectedCallback() {
      this.listPageUrl = this.dataset.listPageUrl || window.location.pathname;
      this.perPage = parseInt(this.dataset.perPage || '24', 10);
      this.paginationMode = this.dataset.paginationMode || 'load_more';

      this.grid = this.querySelector('[data-look-grid]');
      this.emptyEl = this.querySelector('[data-look-empty]');
      this.loadMoreWrap = this.querySelector('[data-load-more-wrap]');
      this.loadMoreBtn = this.querySelector('[data-load-more]');
      this.sentinel = this.querySelector('[data-load-sentinel]');
      this.stickyEl = this.querySelector('.looks-filter__sticky');
      this.filterButtons = Array.from(this.querySelectorAll('[data-look-filter]'));

      if (!this.grid) return;

      this.lookIndex = this._readIndex();
      if (this.lookIndex) {
        this._connectIndexMode();
        return;
      }

      this.allCards = Array.from(this.grid.querySelectorAll('[data-look-card]'));
      this._indexCardCategories();

      // Priority sort (opt-in via data-sort="priority"): higher data-priority
      // first, ties keep their original (creation) order via a stable sort.
      // Reorders the DOM so filtering, batched reveal, and the featured cap all
      // inherit the order. Not applied in manual/random modes.
      if (this.dataset.sort === 'priority') {
        this._sortByPriority();
      }

      this.activeCategory = this._readCategoryFromUrl();
      this.shownCount = 0;

      this._bindEvents();
      this._applyFilter(this.activeCategory, /* pushState */ false);

      // Mark ready so the anti-FOUC guard (grid hidden until sorted) releases.
      this.setAttribute('data-sorted', '');
    }

    _sortByPriority() {
      const priorityOf = (card) => parseInt(card.dataset.priority || '0', 10) || 0;
      // Array.prototype.sort is stable (ES2019+), so equal priorities keep order.
      this.allCards.sort((a, b) => priorityOf(b) - priorityOf(a));
      // Re-append in sorted order — moves existing nodes, no clone.
      this.allCards.forEach((card) => this.grid.appendChild(card));
    }

    /* ---------------------------------------------------------------
     * Index mode
     * ------------------------------------------------------------- */

    _readIndex() {
      const script = this.querySelector('script[data-look-index]');
      if (!script) return null;
      try {
        const list = JSON.parse(script.textContent);
        return Array.isArray(list) ? list : null;
      } catch (_) {
        return null;
      }
    }

    _connectIndexMode() {
      this._cardCache = new Map();
      this._pending = new Map();
      this._loading = false;
      this._requestId = 0;

      this.lookIndex.forEach((entry, i) => {
        entry.categories = (entry.categories || []).map((c) => String(c).trim().toLowerCase()).filter(Boolean);
        entry.priority = parseInt(entry.priority, 10) || 0;
        entry.order = i;
      });

      // Same rule as DOM mode: higher priority first, ties keep index order.
      if (this.dataset.sort === 'priority') {
        this.lookIndex.sort((a, b) => b.priority - a.priority || a.order - b.order);
      }

      Array.from(this.grid.querySelectorAll('[data-look-card]')).forEach((card) => {
        if (card.dataset.handle) this._cardCache.set(card.dataset.handle, card);
      });

      this.activeCategory = this._readCategoryFromUrl();
      this.shownCount = 0;
      this.matchingEntries = [];

      this._bindEvents();
      this._applyIndexFilter(this.activeCategory, /* pushState */ false).finally(() => {
        this.setAttribute('data-sorted', '');
      });
    }

    _applyIndexFilter(category, pushState) {
      this.activeCategory = category;
      this._setActiveButton(category);

      this.matchingEntries = category
        ? this.lookIndex.filter((entry) => entry.categories.includes(category))
        : this.lookIndex.slice();

      if (pushState) this._writeCategoryToUrl(category);
      this._dispatchSwapped(category, this.matchingEntries.length);

      this.shownCount = 0;
      return this._showIndexBatch({ replace: true });
    }

    /**
     * Shows the next `perPage` matching looks. Cards already on the page or
     * fetched earlier are reused; the rest are fetched in parallel. A newer
     * filter change makes an older, still-loading batch stale so it never
     * paints over the current view.
     */
    _showIndexBatch({ replace = false } = {}) {
      const requestId = ++this._requestId;
      const start = this.shownCount;
      const batch = this.matchingEntries.slice(start, start + this.perPage);

      this._setLoading(true);

      return Promise.all(batch.map((entry) => this._getCard(entry))).then((cards) => {
        if (requestId !== this._requestId) return;

        const fragment = document.createDocumentFragment();
        cards.forEach((card) => {
          if (!card) return;
          card.hidden = false;
          fragment.appendChild(card);
        });

        if (replace) {
          this.grid.replaceChildren(fragment);
        } else {
          this.grid.appendChild(fragment);
        }

        this.shownCount = start + batch.length;
        this._setLoading(false);
        this._updateIndexChrome();
      });
    }

    _getCard(entry) {
      const cached = this._cardCache.get(entry.handle);
      if (cached) return Promise.resolve(cached);
      if (this._pending.has(entry.handle)) return this._pending.get(entry.handle);
      if (!entry.url) return Promise.resolve(null);

      const endpoint = entry.url + (entry.url.indexOf('?') === -1 ? '?' : '&') + 'section_id=look-card-item';
      const request = fetch(endpoint, { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.text();
        })
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const card = doc.querySelector('[data-look-card]');
          if (!card) return null;
          const adopted = document.importNode(card, true);
          this._cardCache.set(entry.handle, adopted);
          return adopted;
        })
        .catch(() => null)
        .finally(() => {
          this._pending.delete(entry.handle);
        });

      this._pending.set(entry.handle, request);
      return request;
    }

    _setLoading(isLoading) {
      this._loading = isLoading;
      this.toggleAttribute('data-loading', isLoading);
      if (this.loadMoreBtn) {
        this.loadMoreBtn.disabled = isLoading;
        this.loadMoreBtn.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      }
    }

    _updateIndexChrome() {
      const matched = this.matchingEntries.length;
      const hasMore = this.shownCount < matched;

      if (this.loadMoreWrap) this.loadMoreWrap.hidden = !hasMore;
      if (this.emptyEl) this.emptyEl.hidden = matched > 0;

      this._dispatchLoaded({
        category: this.activeCategory,
        shown: this.shownCount,
        matched,
        hasMore,
      });
    }

    disconnectedCallback() {
      this.filterButtons.forEach((btn) => btn.removeEventListener('click', this._onFilterClick));
      if (this.loadMoreBtn) this.loadMoreBtn.removeEventListener('click', this._onLoadMore);
      window.removeEventListener('popstate', this._onPopState);
      if (this._io) {
        this._io.disconnect();
        this._io = null;
      }
    }

    _bindEvents() {
      this.filterButtons.forEach((btn) => btn.addEventListener('click', this._onFilterClick));
      if (this.loadMoreBtn) this.loadMoreBtn.addEventListener('click', this._onLoadMore);
      window.addEventListener('popstate', this._onPopState);

      const useObserver =
        (this.paginationMode === 'infinite' || this.paginationMode === 'both') &&
        this.sentinel &&
        'IntersectionObserver' in window;

      if (useObserver) {
        this._io = new IntersectionObserver(this._onIntersect, { rootMargin: '300px 0px' });
        this._io.observe(this.sentinel);
      }
    }

    _indexCardCategories() {
      this.allCards.forEach((card) => {
        const raw = (card.dataset.categories || '').toLowerCase();
        const list = raw
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        card._lookCategorySet = new Set(list);
      });
    }

    _readCategoryFromUrl() {
      try {
        const params = new URLSearchParams(window.location.search);
        return (params.get('category') || '').toLowerCase();
      } catch (_) {
        return '';
      }
    }

    _writeCategoryToUrl(category) {
      const url = new URL(window.location.href);
      if (category) {
        url.searchParams.set('category', category);
      } else {
        url.searchParams.delete('category');
      }
      window.history.pushState({ lookCategory: category }, '', url.toString());
    }

    _onFilterClick(e) {
      const btn = e.currentTarget;
      const category = (btn.dataset.lookFilter || '').toLowerCase();

      // Allow CMD/CTRL/middle-click to open in new tab as a normal link
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;

      e.preventDefault();
      if (category === this.activeCategory) return;
      this._filterTo(category, /* pushState */ true);
      // Auto-scroll is meaningful on the dedicated Looks list page where the
      // grid IS the whole page. On the homepage / featured slider it's just
      // disruptive — opt out via data-autoscroll="0".
      if (this.dataset.autoscroll !== '0') {
        this._scrollToGridTop();
      }
    }

    /**
     * Scrolls so the grid's top edge sits flush under the sticky page header
     * + sticky filter row. Reads the sticky's computed `top` (which already
     * resolves --header-height) and
     * adds its own height for the total viewport obstruction.
     */
    _scrollToGridTop() {
      if (!this.grid) return;

      let topOffset = 0;
      if (this.stickyEl) {
        const csTop = parseFloat(getComputedStyle(this.stickyEl).top) || 0;
        topOffset = csTop + this.stickyEl.offsetHeight;
      }

      const gridDocY = this.grid.getBoundingClientRect().top + window.scrollY;
      const target = Math.max(0, gridDocY - topOffset - 40);

      const reduceMotion =
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      window.scrollTo({ top: target, behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    _onPopState() {
      this._filterTo(this._readCategoryFromUrl(), /* pushState */ false);
    }

    _onLoadMore() {
      if (this.lookIndex) {
        if (!this._loading) this._showIndexBatch();
        return;
      }
      this._revealNext();
    }

    _onIntersect(entries) {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        if (this.lookIndex) {
          if (!this._loading && this.shownCount < this.matchingEntries.length) this._showIndexBatch();
          return;
        }
        if (this.shownCount >= this.matchingCards.length) return;
        this._revealNext();
      }
    }

    _filterTo(category, pushState) {
      if (this.lookIndex) {
        this._applyIndexFilter(category, pushState);
      } else {
        this._applyFilter(category, pushState);
      }
    }

    _setActiveButton(category) {
      this.filterButtons.forEach((btn) => {
        const isActive = (btn.dataset.lookFilter || '').toLowerCase() === category;
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }

    _dispatchSwapped(category, count) {
      this.dispatchEvent(
        new CustomEvent('looks:swapped', {
          bubbles: true,
          detail: { category, count },
        })
      );
      // Re-dispatch on document for cross-section listeners (e.g. header)
      document.dispatchEvent(
        new CustomEvent('looks:swapped', {
          detail: { category, count },
        })
      );
    }

    _dispatchLoaded(detail) {
      this.dispatchEvent(new CustomEvent('looks:loaded', { bubbles: true, detail }));
      document.dispatchEvent(new CustomEvent('looks:loaded', { detail }));
    }

    _applyFilter(category, pushState) {
      this.activeCategory = category;
      this._setActiveButton(category);

      this.matchingCards = category
        ? this.allCards.filter((card) => card._lookCategorySet.has(category))
        : this.allCards.slice();

      this.shownCount = 0;
      this._revealNext({ initial: true });

      if (pushState) this._writeCategoryToUrl(category);
      this._dispatchSwapped(category, this.matchingCards.length);
    }

    _revealNext({ initial = false } = {}) {
      const nextCount = Math.min(this.shownCount + this.perPage, this.matchingCards.length);

      // Hide every card first to avoid stale visibility from previous filter state
      if (initial) {
        this.allCards.forEach((card) => {
          card.hidden = true;
        });
      }

      // Show the matching cards up to nextCount
      for (let i = this.shownCount; i < nextCount; i++) {
        this.matchingCards[i].hidden = false;
      }

      this.shownCount = nextCount;
      this._updateChrome();
    }

    _updateChrome() {
      const hasMore = this.shownCount < this.matchingCards.length;

      if (this.loadMoreWrap) this.loadMoreWrap.hidden = !hasMore;
      if (this.emptyEl) this.emptyEl.hidden = this.matchingCards.length > 0;

      this._dispatchLoaded({
        category: this.activeCategory,
        shown: this.shownCount,
        matched: this.matchingCards.length,
        hasMore,
      });
    }
  }

  customElements.define('looks-filter', LooksFilter);
}
